import { PhoneNumberUtil } from 'google-libphonenumber';
import { Errors } from '@hooks/errors/types';
import { errorKeyRegEx, geolocalizationRegEx, passwordRegEx } from './consts';
import { toast } from '@hooks/useToast';
import { SplittedGeolocalization } from '@services/jobs/createJob';
import { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Attribute, InitialAttribute } from './types';

export const useShowErrorToast = () => {
  const { t } = useTranslation('app');

  const showErrorToast = (e: Errors) => {
    const errors = typeof e === 'string' ? e.replace(' ', '%').split('%') : e;
    if (Array.isArray(errors)) {
      toast({
        title: `${errors
          .map((err) => (errorKeyRegEx.test(err) ? t(`errors.${err}`) : err))
          .join('\n')}`,
        variant: 'destructive',
      });
    }
  };

  return {
    showErrorToast,
  };
};

type BaseResp = {
  status?: unknown;
  success?: boolean;
  errors?: unknown;
  message?: string;
  data?: unknown;
};

export const handleOnSettled = <T extends BaseResp>(
  resp: T,
  successHandler: (data?: T) => void,
  setFormErrors: () => void,
  raw?: boolean,
) => {
  if (resp?.success && typeof successHandler === 'function') {
    successHandler((raw ? resp : resp?.data) as T);
    return;
  }
  if (resp?.errors) {
    setFormErrors();
    return;
  }
  if (resp?.message) {
    if (typeof successHandler === 'function') successHandler();
    return;
  }
};

export const validatePassword = (value?: unknown) => {
  if (!value || typeof value !== 'string') return false;
  return passwordRegEx.test(value);
};

export const validateGeolocalization = (value?: unknown) => {
  if (!value || typeof value !== 'string') return false;
  return geolocalizationRegEx.test(value);
};

export const splitGeolocalization = (value: string): SplittedGeolocalization => {
  const [lat, lon] = value.split(',').map((el) => el.trim());
  return { lat: lat || '', lon: lon || '' };
};

export const joinGeolocalization = (value: SplittedGeolocalization) => {
  const { lat, lon } = value;
  if (!lat || !lon) return '';
  return [lat, lon].join(', ');
};

export const hasDirtyValues = (dirty: unknown): boolean => {
  if (typeof dirty === 'boolean') return dirty;
  if (Array.isArray(dirty)) return dirty.some(hasDirtyValues);
  if (dirty && typeof dirty === 'object') return Object.values(dirty).some(hasDirtyValues);
  return false;
};

export const validateFloat = (value: unknown) => {
  if (value && parseFloat(value as string) >= 0) return true;
  return false;
};

export const toInputValue = (value?: string | number | null) =>
  value === null || value === undefined ? '' : String(value);

export const parseDecimalValue = (value: string) =>
  new Intl.NumberFormat('en-US', { useGrouping: false, maximumSignificantDigits: 10 }).format(
    Number(value),
  );

export const validateKeyDown = (e: KeyboardEvent<HTMLInputElement>, pattern: RegExp) => {
  const match = e.key.match(pattern);

  return (
    e.metaKey ||
    e.key.includes('Arrow') ||
    e.key === 'Tab' ||
    e.key === 'Backspace' ||
    e.key === 'Enter' ||
    (match && match['0'] === match.input)
  );
};

export const transformEmptyPhoneNumber = (value: string) => (!value || value === '+1' ? '' : value);

export const validatePhoneNumber = (value: string, optional = false): boolean => {
  const phoneUtil = PhoneNumberUtil.getInstance();

  if (optional && (!value || value === '+1')) return true;

  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(value));
  } catch (_e) {
    return false;
  }
};

const getRemovedAttributes = (curr: string[], prev?: InitialAttribute[]): Attribute[] =>
  prev
    ?.filter((el) => {
      if (curr.includes(el.value)) return false;
      return true;
    })
    .map((el) => ({ ...el, name: el.value, _destroy: '1' })) || [];

const getInitialAttributes = (curr: string[], prev?: InitialAttribute[]): Attribute[] =>
  curr.map((v) => {
    const prevEl = prev?.find((el) => el.value === v);
    if (prevEl) return { ...prevEl, name: prevEl.value } as Attribute;
    return { name: v };
  });

export const parseAttributesToForm = (data?: InitialAttribute[]): string[] =>
  data?.map((el) => el.value) || [];

export const parseAttributesToRequest = (values: string[], data?: InitialAttribute[]) => [
  ...getInitialAttributes(values, data),
  ...getRemovedAttributes(values, data),
];
